import React from 'react';
import JSONPretty from 'react-json-pretty';

import { css } from '@emotion/css';
import { Modal } from '@opendoor/bricks/complex';
import { Box, Heading, Text } from '@opendoor/bricks/core';
import { Table, TableCell, TableRow } from '@opendoor/bricks/core/Table';
import { colors } from '@opendoor/bricks/theme/eero';

import { ILogData } from '../graphql/queries/GetLogs';

interface ILogProps {
  data: ILogData;
  onClick: (log: ILogData) => void;
}

interface ILogCellProps {
  text: string | React.ReactElement;
}

interface IStatusBadgeProps {
  status: string;
}

export const LogCell = ({ text }: ILogCellProps) => {
  return (
    <TableCell textAlign="left" paddingTop={2} paddingBottom={2}>
      {text}
    </TableCell>
  );
};

const StatusBadge = ({ status }: IStatusBadgeProps) => {
  const textColor = status === 'SUCCESS' ? 'green50' : 'terracotta50';

  return (
    <Text ml={2} color={textColor} fontWeight="bold">
      {status.split('_').join(' ')}
    </Text>
  );
};

export const LogRow = ({ data, onClick }: ILogProps) => {
  const { statusCode, operationType, method, path, timestamp } = data;
  const handleClick = (_: React.SyntheticEvent) => {
    onClick(data);
  };

  return (
    <TableRow
      onClick={handleClick}
      className={css`
        cursor: pointer;
        &:hover {
          background-color: ${colors.neutrals10};
        }
      `}
    >
      <LogCell text={<StatusBadge status={statusCode} />} />
      <LogCell
        text={
          <span>
            {operationType ? operationType.toUpperCase() : method}
            &nbsp;&nbsp;&nbsp;&nbsp;
            {path}
          </span>
        }
      />
      <LogCell text={new Date(parseInt(timestamp, 10)).toLocaleString()} />
    </TableRow>
  );
};

interface ILogViewProps {
  isOpen: boolean;
  logData: ILogData;
  handleClose: () => void;
}

const ODTheme = {
  main: `line-height:1.3;color:${colors.neutrals90};background:${colors.neutrals10};word-break:break-word;border-radius:4px;padding:20px;`,
  error: `line-height:1.3;color:${colors.neutrals90};background:${colors.neutrals10};word-break:break-word;border-radius:4px;`,
  key: `color:${colors.neutrals90};`,
  string: `color:${colors.green50};`,
  value: `color:${colors.terracotta50};`,
  boolean: `color:${colors.brand50};`,
};

export const LogViewer = ({ isOpen, logData, handleClose }: ILogViewProps) => {
  const description = logData.operationType
    ? logData.operationType.toUpperCase() + ' ' + logData.path
    : logData.method + ' ' + logData.path;
  return (
    <Modal
      analyticsName="partnership-dashboard-fe-log-viewer-modal"
      isOpen={isOpen}
      shouldCloseOnOverlayClick={true}
      desktopAndTabletSize="lg"
      onRequestClose={handleClose}
      aria-labelledby="log-modal-description"
    >
      <Heading size="UNSAFE_h4" as="h4" id="log-modal-description">
        {description}
      </Heading>
      <Box mt={3}>
        <Text fontWeight="bold" fontSize="s1">
          Details
        </Text>
        <Table width="100%">
          <TableRow>
            <LogCell text="Time" />
            <LogCell text={new Date(parseInt(logData.timestamp, 10)).toLocaleString()} />
          </TableRow>
          <TableRow>
            <LogCell text="Status" />
            <LogCell text={logData.statusCode} />
          </TableRow>
          <TableRow>
            <LogCell text="Method" />
            <LogCell text={logData.method} />
          </TableRow>
          {logData.operationType ? (
            <TableRow>
              <LogCell text="GraphQL Operation" />
              <LogCell text={logData.operationType.toUpperCase()} />
            </TableRow>
          ) : null}
          <TableRow>
            <LogCell text="Path" />
            <LogCell text={logData.path} />
          </TableRow>
          {logData.origin && (
            <TableRow>
              <LogCell text="Origin" />
              <LogCell text={logData.origin} />
            </TableRow>
          )}
          {logData.agent && (
            <TableRow>
              <LogCell text="Device" />
              <LogCell text={logData.agent} />
            </TableRow>
          )}
        </Table>
      </Box>
      <Box mt={3}>
        <Text fontWeight="bold" fontSize="s1">
          Request Body
        </Text>
        <JSONPretty data={logData.requestBody} theme={ODTheme} />
      </Box>
      <Box mt={3}>
        <Text fontWeight="bold" fontSize="s1">
          Response Body
        </Text>
        <JSONPretty data={logData.responseBody} theme={ODTheme} />
      </Box>
    </Modal>
  );
};
